import React, { useMemo, useState } from 'react';
import { Calculator, Info, AlertTriangle, CalendarClock } from 'lucide-react';
import { berechneDurchschnittlicheEegVerguetung, pruefeAktualitaet, EEG_STAND } from '../lib/eegVerguetung';

/**
 * Rechner fuer die durchschnittliche EEG-Verguetung.
 *
 * Bewusst EIN Rechner statt zweier getrennter: Ob Einspeiseverguetung oder
 * Marktpraemie gilt, haengt allein an der Anlagengroesse (100 kWp). Zwei
 * nebeneinanderstehende Rechner wuerden die Verwechslung erst ermoeglichen -
 * hier schaltet die Tabelle automatisch um und wird sichtbar benannt.
 */
export default function EegRechner({ startKwp = '' }) {
  const [kwpEingabe, setKwpEingabe] = useState(String(startKwp || ''));
  const [einspeiseart, setEinspeiseart] = useState('voll');

  // EN:POWER baut ausschliesslich auf Dachflaechen, deshalb hier keine Auswahl.
  // Die Saetze fuer Freiflaechen (§ 48 Abs. 1 EEG) liegen weiterhin in
  // lib/eegVerguetung.js bereit - sollte doch ein solches Projekt kommen,
  // genuegt es, anlagenart: 'sonstige' durchzureichen.
  const anlagenart = 'gebaeude';

  const kwp = useMemo(() => {
    const roh = String(kwpEingabe).replace(/\./g, '').replace(',', '.').trim();
    const zahl = Number.parseFloat(roh);
    return Number.isFinite(zahl) ? zahl : 0;
  }, [kwpEingabe]);

  const ergebnis = useMemo(
    () => berechneDurchschnittlicheEegVerguetung({ kwp, anlagenart, einspeiseart }),
    [kwp, anlagenart, einspeiseart],
  );

  // Einmal pro Aufbau ermitteln - das Datum aendert sich waehrend der Nutzung nicht.
  const aktualitaet = useMemo(() => pruefeAktualitaet(), []);

  // Der Ablaufhinweis steht schon im Banner oben; in der Liste waere er doppelt.
  const weitereHinweise = ergebnis.hinweise.filter((h) => h !== aktualitaet.meldung);

  const hatErgebnis = kwp > 0;
  const zahl = (wert, stellen = 2) => wert.toLocaleString('de-DE', {
    minimumFractionDigits: stellen,
    maximumFractionDigits: stellen,
  });

  return (
    <div style={{ maxWidth: 760, display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Calculator size={20} color="#E07000" />
        <div>
          <h2 style={{ margin: 0, fontSize: 18, color: '#0F172A' }}>
            Durchschnittliche EEG-Vergütung
          </h2>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#64748B' }}>
            {EEG_STAND.bezeichnung}
          </p>
        </div>
      </div>

      {/* Ablaufwarnung ganz oben und unabhaengig von der Eingabe: Wer den
          Rechner oeffnet, soll sofort sehen, dass die Saetze nachzutragen sind -
          nicht erst, nachdem er eine Leistung eingegeben hat. */}
      {aktualitaet.zustand !== 'gueltig' && (
        <div style={{
          display: 'flex', gap: 10, alignItems: 'flex-start',
          background: aktualitaet.zustand === 'abgelaufen' ? '#FEE2E2' : '#FEF3C7',
          color: aktualitaet.zustand === 'abgelaufen' ? '#991B1B' : '#92400E',
          border: `1px solid ${aktualitaet.zustand === 'abgelaufen' ? '#FECACA' : '#FDE68A'}`,
          padding: '12px 14px', borderRadius: 10, fontSize: 13, lineHeight: 1.55,
        }}>
          {aktualitaet.zustand === 'abgelaufen'
            ? <AlertTriangle size={17} style={{ flexShrink: 0, marginTop: 1 }} />
            : <CalendarClock size={17} style={{ flexShrink: 0, marginTop: 1 }} />}
          <div>
            <strong style={{ display: 'block', marginBottom: 2 }}>
              {aktualitaet.zustand === 'abgelaufen'
                ? 'Diese Fördersätze sind nicht mehr gültig'
                : 'Diese Fördersätze laufen bald aus'}
            </strong>
            {aktualitaet.meldung}
            <a
              href={EEG_STAND.quelle}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-block', marginTop: 6, color: 'inherit', fontWeight: 700 }}
            >
              Aktuelle Sätze bei der Bundesnetzagentur ansehen →
            </a>
          </div>
        </div>
      )}

      {/* Eingaben */}
      <div style={{
        background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12,
        padding: 16, display: 'grid', gap: 14,
      }}>
        <label style={{ display: 'grid', gap: 5 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>
            Installierte Leistung (kWp)
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={kwpEingabe}
            onChange={(e) => setKwpEingabe(e.target.value)}
            placeholder="z. B. 750"
            style={{
              padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: 8,
              fontSize: 16, fontFamily: 'inherit', maxWidth: 220,
            }}
          />
        </label>

        <Auswahl
          titel="Einspeisung"
          wert={einspeiseart}
          setzeWert={setEinspeiseart}
          optionen={[
            { wert: 'voll', label: 'Volleinspeisung (Standard)' },
            { wert: 'teil', label: 'Teileinspeisung' },
          ]}
        />
      </div>

      {/* Ergebnis */}
      {hatErgebnis && (
        <div style={{
          background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12,
          padding: 16, display: 'grid', gap: 14,
          borderLeft: '4px solid #E07000',
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 34, fontWeight: 800, color: '#0F172A', lineHeight: 1 }}>
              {zahl(ergebnis.durchschnitt, 2)}
            </span>
            <span style={{ fontSize: 16, color: '#475569' }}>ct/kWh</span>
            <span style={{
              background: '#FFF7ED', color: '#9A3412', fontSize: 12, fontWeight: 700,
              padding: '3px 10px', borderRadius: 999,
            }}>
              {ergebnis.verguetungsartLabel}
            </span>
          </div>

          {/* Aufschluesselung: macht nachvollziehbar, wie der Mischwert entsteht */}
          {ergebnis.baender.length > 1 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 420 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#64748B', fontSize: 12 }}>
                    <th style={zelle}>Leistungsanteil</th>
                    <th style={{ ...zelle, textAlign: 'right' }}>kWp</th>
                    <th style={{ ...zelle, textAlign: 'right' }}>Satz</th>
                    <th style={{ ...zelle, textAlign: 'right' }}>Anteil am Erlös</th>
                  </tr>
                </thead>
                <tbody>
                  {ergebnis.baender.map((band) => (
                    <tr key={`${band.vonKwp}-${band.bisKwp}`} style={{ borderTop: '1px solid #F1F5F9' }}>
                      <td style={zelle}>
                        {band.vonKwp === 0 ? 'bis' : `${zahl(band.vonKwp, 0)} – `}
                        {band.vonKwp === 0 ? ` ${zahl(band.bisKwp, 0)}` : zahl(band.bisKwp, 0)} kWp
                      </td>
                      <td style={{ ...zelle, textAlign: 'right' }}>{zahl(band.anteilKwp, 1)}</td>
                      <td style={{ ...zelle, textAlign: 'right' }}>{zahl(band.satz, 2)} ct</td>
                      <td style={{ ...zelle, textAlign: 'right', color: '#64748B' }}>
                        {zahl(band.ertragsanteil, 1)} %
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {weitereHinweise.map((hinweis) => (
            <div
              key={hinweis}
              style={{
                display: 'flex', gap: 8, alignItems: 'flex-start',
                background: /Ausschreibung/.test(hinweis) ? '#FEF3C7' : '#F8FAFC',
                color: /Ausschreibung/.test(hinweis) ? '#92400E' : '#475569',
                fontSize: 12, padding: '8px 10px', borderRadius: 8, lineHeight: 1.5,
              }}
            >
              {/Ausschreibung/.test(hinweis)
                ? <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                : <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />}
              <span>{hinweis}</span>
            </div>
          ))}
        </div>
      )}

      <p style={{ margin: 0, fontSize: 11, color: '#94A3B8', lineHeight: 1.6 }}>
        Das EEG vergütet anteilig je Leistungsband, nicht pauschal nach Anlagengröße
        ({EEG_STAND.fundstelle}). Der angezeigte Wert ist der daraus gemittelte Satz.
        Ab 100 kWp greift automatisch die Marktprämien-Tabelle, weil dort die
        Direktvermarktung verpflichtend ist.
      </p>
    </div>
  );
}

const zelle = { padding: '7px 10px' };

function Auswahl({ titel, wert, setzeWert, optionen }) {
  return (
    <div style={{ display: 'grid', gap: 5 }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{titel}</span>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {optionen.map((option) => {
          const aktiv = wert === option.wert;
          return (
            <button
              key={option.wert}
              type="button"
              onClick={() => setzeWert(option.wert)}
              style={{
                padding: '7px 13px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                fontFamily: 'inherit', fontWeight: aktiv ? 700 : 500,
                border: `1px solid ${aktiv ? '#E07000' : '#CBD5E1'}`,
                background: aktiv ? '#FFF7ED' : '#fff',
                color: aktiv ? '#9A3412' : '#475569',
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
